import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { CrudPage } from "../Crud";
import { Field } from "../scouting/parts";
import { X, Loader2, ClipboardCheck, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Materials() {
  const [reload, setReload] = useState(0);
  const [queue, setQueue] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => {
    api.get("/materials").then(({ data }) => setQueue(data.filter((m) => m.active && m.registration_status !== "verified")));
  }, [reload]);
  return (
    <div className="space-y-4">
      {queue.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-500 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="verify-banner">
          <div>
            <div className="font-bold uppercase text-sm text-amber-900">{queue.length} material{queue.length > 1 ? "s" : ""} need label verification</div>
            <div className="text-xs text-amber-800 mt-0.5">Confirm rates, REI/PHI and PPE against each registered label in the <a href="https://www.cdms.net/Label-Database" target="_blank" rel="noreferrer" className="underline font-bold">CDMS label database</a> before these are used on field sheets.</div>
          </div>
          <div className="flex gap-2">
            <button data-testid="ai-fill-all-btn" disabled={bulkBusy}
              onClick={async () => {
                setBulkBusy(true);
                try {
                  const { data } = await api.post("/materials/backfill-labels", {}, { timeout: 300000 });
                  toast.success(`AI processed ${data.processed} labels — ${data.verified} verified, ${data.filled} filled, ${data.failed} need manual entry`);
                  setReload((r) => r + 1);
                } catch (e) { toast.error("Bulk lookup failed — try again"); }
                finally { setBulkBusy(false); }
              }}
              className="h-12 px-4 border-2 border-[#1F3D34] bg-white text-[#1F3D34] font-bold uppercase text-xs flex items-center gap-2 disabled:opacity-40">
              {bulkBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} {bulkBusy ? "Searching labels…" : "AI Fill All"}
            </button>
            <button data-testid="verify-labels-btn" onClick={() => setWizardOpen(true)}
              className="h-12 px-5 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center gap-2">
              <ClipboardCheck size={15} /> Verify Labels
            </button>
          </div>
        </div>
      )}
      <CrudPage
        key={reload}
        resource="materials"
        title="Approved Materials"
        defaultBody={{ product: "", supplier: "Wilbur-Ellis", activity: "", targets: "", epa_reg_no: "", active_ingredient: "", frac_group: "", irac_group: "",
          crop: "Grape", rate_min: 0, rate_max: 0, rate_unit: "", rei_hours: 0, phi_days: 0,
          min_retreat_days: 0, max_applications: 0, restrictions: "", label_url: "",
          registration_status: "needs_review", last_verified: "", active: true }}
        columns={[
          { key: "product", label: "Product" },
          { key: "supplier", label: "Supplier" },
          { key: "activity", label: "Activity" },
          { key: "organic", label: "Organic" },
          { key: "material_type", label: "Type" },
          { key: "active_ingredient", label: "Active Ingredient" },
          { key: "frac_group", label: "FRAC" },
          { key: "irac_group", label: "IRAC" },
          { key: "rei_hours", label: "REI (h)" },
          { key: "phi_days", label: "PHI (d)" },
          { key: "min_retreat_days", label: "Min Retreat (d)" },
          { key: "registration_status", label: "Status" },
          { key: "active", label: "Active" },
        ]}
        fields={[
          { key: "product", label: "Product Name" },
          { key: "supplier", label: "Supplier / Distributor (e.g. Wilbur-Ellis)" },
          { key: "activity", label: "Activity", type: "select", options: [
            { value: "", label: "—" }, { value: "Preventative", label: "Preventative" },
            { value: "Curative", label: "Curative" }, { value: "Preventative + Curative", label: "Preventative + Curative" }] },
          { key: "targets", label: "Target Pests (comma-separated, e.g. Powdery mildew, Botrytis)" },
          { key: "organic", label: "Organic / OMRI Listed", type: "checkbox" },
          { key: "material_type", label: "Material Type", type: "select", options: [
            { value: "Pesticide", label: "Pesticide" }, { value: "Spreader/Sticker", label: "Adjuvant — Spreader/Sticker" },
            { value: "Drift Reduction", label: "Adjuvant — Drift Reduction" }, { value: "Adjuvant/Other", label: "Adjuvant — Other" }] },
          { key: "epa_reg_no", label: "EPA Registration #" },
          { key: "active_ingredient", label: "Active Ingredient" },
          { key: "frac_group", label: "FRAC Group (e.g. M02, 3, 7)" },
          { key: "irac_group", label: "IRAC Group" },
          { key: "crop", label: "Crop" },
          { key: "rate_min", label: "Rate Min", type: "number" },
          { key: "rate_max", label: "Rate Max", type: "number" },
          { key: "rate_unit", label: "Rate Unit (e.g. lb/ac)" },
          { key: "rei_hours", label: "REI (hours)", type: "number" },
          { key: "phi_days", label: "PHI (days)", type: "number" },
          { key: "min_retreat_days", label: "Min Retreatment Interval (days)", type: "number" },
          { key: "max_applications", label: "Max Applications / Season", type: "number" },
          { key: "restrictions", label: "Restrictions" },
          { key: "incompatible_with", label: "Incompatible With (comma-separated keywords from label/CDMS, e.g. oil, sulfur)" },
          { key: "compatibility_notes", label: "Compatibility Notes (from label/CDMS)" },
          { key: "formulation", label: "Formulation (e.g. 80DF, EC)" },
          { key: "signal_word", label: "Signal Word (from label)" },
          { key: "ppe_applicator", label: "PPE — Applicator/Handler (from label)" },
          { key: "ppe_mixer", label: "PPE — Mixer/Loader (from label)" },
          { key: "mixing_instructions", label: "Mixing/Loading Instructions (from label)" },
          { key: "label_url", label: "Label URL" },
          { key: "registration_status", label: "Status", type: "select", options: [
            { value: "needs_review", label: "Needs Review" }, { value: "verified", label: "Verified vs Label" }] },
          { key: "last_verified", label: "Last Verified (YYYY-MM-DD)" },
          { key: "active", label: "Active", type: "checkbox" },
        ]}
      />
      {wizardOpen && <VerifyWizard queue={queue} onClose={() => { setWizardOpen(false); setReload((r) => r + 1); }} />}
    </div>
  );
}

const inp = "w-full h-11 px-3 border border-stone-300 text-sm";

function VerifyWizard({ queue, onClose }) {
  const [i, setI] = useState(0);
  const [f, setF] = useState({ ...queue[0] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const [done, setDone] = useState({ verified: 0, skipped: 0 });
  const m = queue[i];
  useEffect(() => { if (queue[i]) { setF({ ...queue[i] }); setErr(""); setAiNote(""); } }, [i]); // eslint-disable-line
  if (!m) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const aiLookup = async () => {
    setAiBusy(true); setErr("");
    try {
      const { data } = await api.post(`/materials/${m.id}/label-lookup`);
      setF((s) => ({
        ...s,
        rate_min: data.rate_min ?? s.rate_min, rate_max: data.rate_max ?? s.rate_max,
        rate_unit: data.rate_unit || s.rate_unit, rei_hours: data.rei_hours ?? s.rei_hours,
        phi_days: data.phi_days ?? s.phi_days, signal_word: data.signal_word || s.signal_word,
        frac_group: data.frac_group || s.frac_group, irac_group: data.irac_group || s.irac_group,
        max_applications: data.max_applications ?? s.max_applications,
        min_retreat_days: data.min_retreat_days ?? s.min_retreat_days,
        restrictions: data.restrictions || s.restrictions,
        ppe_applicator: data.ppe_applicator || s.ppe_applicator,
        ppe_mixer: data.ppe_mixer || s.ppe_mixer,
      }));
      setAiNote(`AI-drafted (confidence: ${data.confidence || "unknown"})${data.notes ? ` — ${data.notes}` : ""}. Verify against the CDMS/Agrian label before saving — the label is the law.`);
    } catch (e) { setErr(e.response?.data?.detail || "Lookup failed — enter values from the label manually"); }
    finally { setAiBusy(false); }
  };
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const filled = (v) => v !== "" && v != null;
  const isAdjuvant = (m.material_type || "Pesticide") !== "Pesticide";
  const canVerify = isAdjuvant
    ? filled(f.rate_min) && filled(f.rate_max) && f.rate_unit
    : filled(f.rate_min) && filled(f.rate_max) && f.rate_unit && filled(f.rei_hours) && filled(f.phi_days) && f.ppe_applicator;
  const advance = (verified) => {
    setDone((d) => ({ verified: d.verified + (verified ? 1 : 0), skipped: d.skipped + (verified ? 0 : 1) }));
    if (i + 1 < queue.length) setI(i + 1);
    else onClose();
  };
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = { ...f };
      delete body.id; delete body.created_at; delete body.label_complete;
      body.rate_min = num(f.rate_min); body.rate_max = num(f.rate_max);
      body.rei_hours = num(f.rei_hours); body.phi_days = num(f.phi_days);
      body.min_retreat_days = num(f.min_retreat_days); body.max_applications = num(f.max_applications);
      body.registration_status = "verified";
      body.last_verified = new Date().toISOString().slice(0, 10);
      await api.put(`/materials/${m.id}`, body);
      advance(true);
    } catch (e) { setErr(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white border border-stone-200 w-full max-w-xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="verify-wizard">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Verify Label — {i + 1} of {queue.length}</h3>
          <button onClick={onClose} data-testid="verify-close"><X size={20} /></button>
        </div>
        <div className="bg-stone-50 border border-stone-200 p-3">
          <div className="font-bold">{m.product} {m.formulation ? <span className="text-stone-400 font-normal text-xs">{m.formulation}</span> : null}</div>
          <div className="text-xs text-stone-600">
            {m.active_ingredient || "AI not entered"}{m.frac_group ? ` · FRAC ${m.frac_group}` : ""}{m.irac_group ? ` · IRAC ${m.irac_group}` : ""}
            {m.activity ? ` · ${m.activity}` : ""}{m.supplier ? ` · ${m.supplier}` : ""}
          </div>
          <div className="flex flex-wrap gap-3 mt-1 items-center">
            <button type="button" data-testid="vw-ai-lookup" onClick={aiLookup} disabled={aiBusy}
              className="h-9 px-3 bg-[#1F3D34] text-white text-[10px] font-bold uppercase flex items-center gap-1 disabled:opacity-40">
              {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {aiBusy ? "Searching labels…" : "AI Label Lookup"}
            </button>
            <a href={`https://www.cdms.net/Label-Database?q=${encodeURIComponent(m.product)}`} target="_blank" rel="noreferrer" data-testid="vw-cdms-link" className="text-xs font-bold text-[#1F3D34] underline flex items-center gap-1"><ExternalLink size={11} /> CDMS</a>
            <a href="https://home.agrian.com/" target="_blank" rel="noreferrer" data-testid="vw-agrian-link" className="text-xs font-bold text-[#1F3D34] underline flex items-center gap-1"><ExternalLink size={11} /> Agrian</a>
            {f.label_url && <a href={f.label_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#1F3D34] underline flex items-center gap-1"><ExternalLink size={11} /> Open Label</a>}
          </div>
        </div>
        {aiNote && <div className="bg-amber-50 border border-amber-400 text-amber-900 p-2 text-[11px] font-semibold" data-testid="vw-ai-note">{aiNote}</div>}
        <div className="text-[11px] text-stone-500">Enter rates, REI and PHI exactly as printed on the registered label (CDMS label database is the reference). All starred fields are required to mark verified.</div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Rate Min *"><input data-testid="vw-rate-min" value={f.rate_min ?? ""} onChange={(e) => set("rate_min", e.target.value)} className={inp} /></Field>
          <Field label="Rate Max *"><input data-testid="vw-rate-max" value={f.rate_max ?? ""} onChange={(e) => set("rate_max", e.target.value)} className={inp} /></Field>
          <Field label="Rate Unit *"><input data-testid="vw-rate-unit" value={f.rate_unit ?? ""} onChange={(e) => set("rate_unit", e.target.value)} placeholder="lb/ac" className={inp} /></Field>
          <Field label="REI (hours) *"><input data-testid="vw-rei" value={f.rei_hours ?? ""} onChange={(e) => set("rei_hours", e.target.value)} className={inp} /></Field>
          <Field label="PHI (days) *"><input data-testid="vw-phi" value={f.phi_days ?? ""} onChange={(e) => set("phi_days", e.target.value)} className={inp} /></Field>
          <Field label="Signal Word"><input value={f.signal_word ?? ""} onChange={(e) => set("signal_word", e.target.value)} placeholder="CAUTION" className={inp} /></Field>
          <Field label="EPA Reg. No."><input value={f.epa_reg_no ?? ""} onChange={(e) => set("epa_reg_no", e.target.value)} className={inp} /></Field>
          <Field label="Min Retreat (d)"><input value={f.min_retreat_days ?? ""} onChange={(e) => set("min_retreat_days", e.target.value)} className={inp} /></Field>
          <Field label="Max Apps / Season"><input value={f.max_applications ?? ""} onChange={(e) => set("max_applications", e.target.value)} className={inp} /></Field>
        </div>
        <Field label="PPE — Applicator/Handler *"><textarea data-testid="vw-ppe-applicator" value={f.ppe_applicator ?? ""} onChange={(e) => set("ppe_applicator", e.target.value)} rows={2} className="w-full border border-stone-300 p-2.5 text-sm" placeholder="Long-sleeved shirt, long pants, chemical-resistant gloves…" /></Field>
        <Field label="PPE — Mixer/Loader"><textarea value={f.ppe_mixer ?? ""} onChange={(e) => set("ppe_mixer", e.target.value)} rows={2} className="w-full border border-stone-300 p-2.5 text-sm" /></Field>
        <Field label="Mixing/Loading Instructions"><textarea value={f.mixing_instructions ?? ""} onChange={(e) => set("mixing_instructions", e.target.value)} rows={2} className="w-full border border-stone-300 p-2.5 text-sm" /></Field>
        <Field label="Restrictions"><input value={f.restrictions ?? ""} onChange={(e) => set("restrictions", e.target.value)} className={inp} /></Field>
        <Field label="Label URL"><input value={f.label_url ?? ""} onChange={(e) => set("label_url", e.target.value)} className={inp} placeholder="https://…" /></Field>
        {err && <div className="bg-red-50 border border-red-400 text-red-800 p-2 text-xs font-semibold" data-testid="vw-error">{err}</div>}
        <div className="flex gap-2">
          <button data-testid="vw-skip-btn" onClick={() => advance(false)} disabled={busy}
            className="h-12 px-5 border border-stone-300 bg-white font-bold uppercase text-xs disabled:opacity-40">Skip</button>
          <button data-testid="vw-verify-btn" onClick={save} disabled={busy || !canVerify}
            className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase text-xs disabled:opacity-40 flex items-center justify-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />} Confirm Verified vs Label
          </button>
        </div>
        {!canVerify && <div className="text-[10px] text-amber-700 font-semibold">{isAdjuvant ? "Rates and unit are required before this adjuvant can be marked verified." : "Rates, REI, PHI and applicator PPE are required before this can be marked verified."}</div>}
        <div className="text-[10px] text-stone-400">{done.verified} verified · {done.skipped} skipped this session</div>
      </div>
    </div>
  );
}
